import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Permission, Role } from '@foundation/shared';
import { can } from '@foundation/shared';
import { ApiRequestError, api, setUnauthenticatedHandler, tokenStore } from './lib/api';
import { getIdToken, initLiff, isDevMode, isLoggedIn, login as liffLogin } from './lib/liff';

export interface StaffProfile {
  id: string;
  staffCode: string;
  name: string;
  role: Role;
  teamId: string | null;
  areaIds: string[];
}

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: StaffProfile }
  | { kind: 'not_linked' }
  | { kind: 'error'; error: unknown };

interface AuthState {
  status: Status;
  retry: () => void;
  allowed: (p: Permission) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const authenticate = useCallback(async () => {
    setStatus({ kind: 'loading' });
    try {
      await initLiff();

      if (!isLoggedIn()) {
        liffLogin();
        return; // เบราว์เซอร์กำลังเปลี่ยนหน้าไป LINE Login
      }

      const idToken = getIdToken();
      if (!idToken) {
        throw new ApiRequestError('LINE_VERIFY_FAILED', 401);
      }

      const res = await api<{ token: string; profile: StaffProfile }>('/auth/liff', {
        method: 'POST',
        anonymous: true,
        body: { idToken, app: 'staff' },
      });

      tokenStore.set(res.token);
      setStatus({ kind: 'ready', profile: res.profile });
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'STAFF_NOT_LINKED') {
        setStatus({ kind: 'not_linked' });
        return;
      }
      setStatus({ kind: 'error', error: err });
    }
  }, []);

  useEffect(() => {
    void authenticate();
  }, [authenticate, attempt]);

  useEffect(() => {
    setUnauthenticatedHandler(() => setAttempt((n) => n + 1));
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      retry: () => setAttempt((n) => n + 1),
      allowed: (p: Permission) => (status.kind === 'ready' ? can(status.profile.role, p) : false),
    }),
    [status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth ต้องอยู่ภายใน AuthProvider');
  return ctx;
}

export function useProfile(): StaffProfile {
  const { status } = useAuth();
  if (status.kind !== 'ready') throw new Error('ยังไม่ได้ยืนยันตัวตน');
  return status.profile;
}

export { isDevMode };
